import { useState } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, resolveTheme } from './designTokens';
import SectionCard from './components/primitives/SectionCard';
import { darkPrimary } from './components/primitives/DarkHeader';
import Button from './components/primitives/Button';
import EmptyState from './components/primitives/EmptyState';
import FormInput from './components/primitives/FormInput';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';

// ─── Video helpers ────────────────────────────────────────────────────────────
function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

function detectVideoSource(url) {
  if (!url) return null;
  if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo')) return 'vimeo';
  return 'other';
}

function VideoEmbed({ url }) {
  const embedUrl = getEmbedUrl(url);
  if (!embedUrl) return <p style={{ color: NEUTRAL.faint, fontSize: '0.82em', margin: '8px 0 0' }}>Ogiltig video-URL</p>;
  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginTop: '12px', borderRadius: '8px', overflow: 'hidden' }}>
      <iframe
        src={embedUrl}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Exercise video"
      />
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────
const BLOCK_TYPE_LABELS = {
  video: 'Video',
  step: 'Steg',
  gallery: 'Bildgalleri',
  tip: 'Tips',
};

const ADD_BLOCK_TYPES = [
  { type: 'video', label: '+ Video' },
  { type: 'step', label: '+ Steg' },
  { type: 'gallery', label: '+ Bildgalleri' },
  { type: 'tip', label: '+ Tips' },
];

const TONE_STYLES = {
  info:    { bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' },
  warning: { bg: STATUS_COLORS.warning_bg, border: '#fcd34d', text: STATUS_COLORS.warning },
  success: { bg: STATUS_COLORS.success_bg, border: '#86efac', text: '#166534' },
};

// ─── Migration layer ──────────────────────────────────────────────────────────
function initBlocks(exercise) {
  if (!exercise) return [];
  const blocks = [];
  let order = 0;
  if (exercise.video_url) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'video',
      order: order++,
      video_url: exercise.video_url,
      video_source: detectVideoSource(exercise.video_url),
      video_label: '',
    });
  }
  if (exercise.description) {
    const stepBlock = {
      id: crypto.randomUUID(),
      type: 'step',
      order: order++,
      title: 'Instruktion',
      body: exercise.description,
      images: [],
    };
    if (exercise.image_url) {
      stepBlock.images = [{ id: crypto.randomUUID(), url: exercise.image_url, alt: '', order: 0 }];
    }
    blocks.push(stepBlock);
  } else if (exercise.image_url) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'gallery',
      order: order++,
      images: [{ id: crypto.randomUUID(), url: exercise.image_url, alt: '', order: 0 }],
    });
  }
  return blocks;
}

// ─── Shared style helpers ─────────────────────────────────────────────────────
const fieldLabelStyle = {
  display: 'block',
  fontSize: '0.82em',
  fontWeight: '600',
  color: NEUTRAL.text,
  marginBottom: '4px',
};

function styledTextarea(t) {
  return {
    width: '100%',
    padding: '9px 12px',
    borderRadius: '7px',
    border: `1px solid ${t.card_border}`,
    fontSize: '0.9em',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    lineHeight: '1.5',
  };
}

function reorderBtnStyle(disabled) {
  return {
    background: 'transparent',
    border: `1px solid ${NEUTRAL.line}`,
    borderRadius: '5px',
    padding: '2px 7px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.75em',
    color: disabled ? NEUTRAL.line : NEUTRAL.muted,
    lineHeight: '1.5',
  };
}

// ─── Block view components ────────────────────────────────────────────────────
function VideoBlockView({ block, theme }) {
  if (!block.video_url) {
    return <p style={{ color: theme.text_secondary, fontSize: '0.875em', margin: 0 }}>Ingen video tillagd — klicka Redigera</p>;
  }
  return (
    <div>
      {block.video_label && (
        <p style={{ margin: '0 0 8px', fontSize: '0.875em', color: theme.text_secondary }}>{block.video_label}</p>
      )}
      <VideoEmbed url={block.video_url} />
    </div>
  );
}

function StepBlockView({ block, theme }) {
  const isEmpty = !block.title && !block.body && !block.images?.length;
  if (isEmpty) {
    return <p style={{ color: theme.text_secondary, fontSize: '0.875em', margin: 0 }}>Tomt steg — klicka Redigera för att lägga till innehåll</p>;
  }
  return (
    <div>
      {block.title && (
        <h4 style={{ margin: '0 0 8px', fontSize: '0.95em', fontWeight: '700', color: NEUTRAL.ink }}>{block.title}</h4>
      )}
      {block.body && (
        <p style={{ margin: '0 0 12px', fontSize: '0.9em', lineHeight: '1.6', color: NEUTRAL.text, whiteSpace: 'pre-wrap' }}>{block.body}</p>
      )}
      {block.images?.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {block.images.map(img => (
            <img key={img.id} src={img.url} alt={img.alt || ''} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${NEUTRAL.line}` }} />
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryBlockView({ block, theme }) {
  if (!block.images?.length) {
    return <p style={{ color: theme.text_secondary, fontSize: '0.875em', margin: 0 }}>Inga bilder tillagda — klicka Redigera</p>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
      {block.images.map(img => (
        <img key={img.id} src={img.url} alt={img.alt || ''} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${NEUTRAL.line}` }} />
      ))}
    </div>
  );
}

function TipBlockView({ block }) {
  const s = TONE_STYLES[block.tone || 'info'];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '8px', padding: '12px 16px' }}>
      <p style={{ margin: 0, color: s.text, fontSize: '0.9em', lineHeight: '1.5' }}>
        {block.body || 'Tomt tips — klicka Redigera för att lägga till text'}
      </p>
    </div>
  );
}

// ─── Block editor components ──────────────────────────────────────────────────
function VideoBlockEditor({ block, theme, onChange }) {
  return (
    <div>
      <FormInput
        theme={theme}
        label="YouTube/Vimeo-länk"
        value={block.video_url || ''}
        onChange={e => onChange({ video_url: e.target.value, video_source: detectVideoSource(e.target.value) })}
        placeholder="https://www.youtube.com/watch?v=..."
      />
      <FormInput
        theme={theme}
        label="Etikett (valfritt)"
        value={block.video_label || ''}
        onChange={e => onChange({ video_label: e.target.value })}
        placeholder="T.ex. Genomgång"
        style={{ marginBottom: '12px' }}
      />
      {block.video_url && <VideoEmbed url={block.video_url} />}
    </div>
  );
}

function StepBlockEditor({ block, theme, onChange, onUploadImage }) {
  return (
    <div>
      <FormInput
        theme={theme}
        label="Rubrik (valfritt)"
        value={block.title || ''}
        onChange={e => onChange({ title: e.target.value })}
        placeholder="T.ex. Utförande"
      />
      <div style={{ marginBottom: '14px' }}>
        <label style={fieldLabelStyle}>Instruktion</label>
        <textarea
          value={block.body || ''}
          onChange={e => onChange({ body: e.target.value })}
          rows={4}
          style={styledTextarea(theme)}
          placeholder="Beskriv steg för steg..."
        />
      </div>
      {block.images?.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {block.images.map(img => (
            <img key={img.id} src={img.url} alt={img.alt || ''} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${theme.card_border}` }} />
          ))}
        </div>
      )}
      <Button theme={theme} variant="secondary" size="sm" onClick={onUploadImage}>
        {block.images?.length ? '+ Fler bilder' : '+ Lägg till bild'}
      </Button>
    </div>
  );
}

function GalleryBlockEditor({ block, theme, onChange, onUploadImage }) {
  return (
    <div>
      {block.images?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px', marginBottom: '14px' }}>
          {block.images.map((img, idx) => (
            <div key={img.id} style={{ position: 'relative' }}>
              <img src={img.url} alt={img.alt || ''} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${theme.card_border}`, display: 'block' }} />
              <button
                onClick={() => onChange({ images: block.images.filter((_, i) => i !== idx) })}
                style={{ position: 'absolute', top: '3px', right: '3px', background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', color: 'white', width: '20px', height: '20px', fontSize: '0.65em', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <Button theme={theme} variant="secondary" size="sm" onClick={onUploadImage}>+ Lägg till bild</Button>
    </div>
  );
}

function TipBlockEditor({ block, theme, onChange }) {
  const TONES = [
    { value: 'info',    label: 'Info' },
    { value: 'warning', label: 'Obs!' },
    { value: 'success', label: 'Bra att veta' },
  ];
  return (
    <div>
      <div style={{ marginBottom: '14px' }}>
        <label style={fieldLabelStyle}>Meddelande</label>
        <textarea
          value={block.body || ''}
          onChange={e => onChange({ body: e.target.value })}
          rows={3}
          style={styledTextarea(theme)}
          placeholder="Tips, påminnelse eller viktig info..."
        />
      </div>
      <div>
        <label style={fieldLabelStyle}>Ton</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {TONES.map(tone => {
            const s = TONE_STYLES[tone.value];
            const isActive = (block.tone || 'info') === tone.value;
            return (
              <button
                key={tone.value}
                onClick={() => onChange({ tone: tone.value })}
                style={{
                  background: isActive ? s.bg : 'white',
                  border: `1px solid ${isActive ? s.border : theme.card_border}`,
                  borderRadius: '6px',
                  color: isActive ? s.text : NEUTRAL.muted,
                  padding: '5px 12px',
                  fontSize: '0.82em',
                  fontWeight: isActive ? '600' : '400',
                  cursor: 'pointer',
                }}
              >
                {tone.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Block render helpers ─────────────────────────────────────────────────────
function renderBlockView(block, theme) {
  switch (block.type) {
    case 'video':   return <VideoBlockView block={block} theme={theme} />;
    case 'step':    return <StepBlockView block={block} theme={theme} />;
    case 'gallery': return <GalleryBlockView block={block} theme={theme} />;
    case 'tip':     return <TipBlockView block={block} />;
    default:        return null;
  }
}

function renderBlockEditor(block, theme, onChange, onUploadImage) {
  const props = { block, theme, onChange, onUploadImage };
  switch (block.type) {
    case 'video':   return <VideoBlockEditor {...props} />;
    case 'step':    return <StepBlockEditor {...props} />;
    case 'gallery': return <GalleryBlockEditor {...props} />;
    case 'tip':     return <TipBlockEditor {...props} />;
    default:        return null;
  }
}

// ─── ExerciseBuilder ──────────────────────────────────────────────────────────
export default function ExerciseBuilder({ exercise, therapist, accentColor = KLINIO_DEFAULT_ACCENT, onBack, onSaved }) {
  const [hero, setHero] = useState({
    title:       exercise?.title || '',
    description: exercise?.description || '',
    category:    exercise?.category || '',
    duration:    '',
    sets:        '',
  });
  const [blocks, setBlocks] = useState(() =>
    exercise?.blocks?.length > 0 ? exercise.blocks : initBlocks(exercise)
  );
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [saving, setSaving] = useState(false);

  const t = resolveTheme(accentColor);

  // ── Block helpers ──
  function moveBlock(id, direction) {
    setBlocks(prev => {
      const arr = [...prev];
      const idx = arr.findIndex(b => b.id === id);
      const target = idx + direction;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr.map((b, i) => ({ ...b, order: i }));
    });
  }

  function updateBlock(id, changes) {
    setBlocks(prev => prev.map(b => b.id === id ? { ...b, ...changes } : b));
  }

  function removeBlock(id) {
    setBlocks(prev => prev.filter(b => b.id !== id).map((b, i) => ({ ...b, order: i })));
    if (editingBlockId === id) setEditingBlockId(null);
  }

  function addBlock(type) {
    const defaults = {
      video:   { video_url: '', video_source: null, video_label: '' },
      step:    { title: '', body: '', images: [] },
      gallery: { images: [] },
      tip:     { body: '', tone: 'info' },
    };
    const newBlock = { id: crypto.randomUUID(), type, order: blocks.length, ...defaults[type] };
    setBlocks(prev => [...prev, newBlock]);
    setEditingBlockId(newBlock.id);
  }

  async function uploadImageToBlock(blockId) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const fileExt = file.name.split('.').pop();
      const fileName = `${therapist.id}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('exercises').upload(fileName, file);
      if (uploadError) { showToast('Fel vid uppladdning: ' + uploadError.message, 'error'); return; }
      const { data: urlData } = supabase.storage.from('exercises').getPublicUrl(fileName);
      setBlocks(prev => prev.map(b => {
        if (b.id !== blockId) return b;
        const newImg = { id: crypto.randomUUID(), url: urlData.publicUrl, alt: '', order: (b.images || []).length };
        return { ...b, images: [...(b.images || []), newImg] };
      }));
    };
    input.click();
  }

  // ── Save ──
  async function handleSave() {
    if (!hero.title.trim()) return;
    setSaving(true);
    const videoBlock = blocks.find(b => b.type === 'video');
    const payload = {
      title:       hero.title.trim(),
      description: hero.description.trim() || null,
      category:    hero.category.trim() || null,
      video_url:   videoBlock?.video_url?.trim() || null,
      blocks,
    };

    if (exercise?.id) {
      const { error } = await supabase.from('exercises').update(payload).eq('id', exercise.id);
      setSaving(false);
      if (error) { showToast('Fel: ' + error.message, 'error'); return; }
      showToast('Övning sparad', 'success');
      onSaved?.({ ...exercise, ...payload });
    } else {
      const { data, error } = await supabase
        .from('exercises')
        .insert({ therapist_id: therapist.id, ...payload })
        .select()
        .single();
      setSaving(false);
      if (error) { showToast('Fel: ' + error.message, 'error'); return; }
      showToast('Övning skapad', 'success');
      onSaved?.(data);
    }
  }

  // ── Hero input styles (dark background) ──
  const heroTitleInput = {
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 0,
    color: '#FFFFFF',
    fontFamily: 'var(--font-heading)',
    fontSize: '24px',
    fontWeight: '800',
    letterSpacing: '-0.01em',
    padding: '4px 0',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '10px',
    display: 'block',
  };

  const heroTextareaStyle = {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    color: t.sidebar_text,
    fontSize: '0.875em',
    padding: '8px 10px',
    width: '100%',
    outline: 'none',
    resize: 'none',
    boxSizing: 'border-box',
    marginBottom: '14px',
    lineHeight: '1.5',
    display: 'block',
  };

  const heroSmallInput = {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: t.sidebar_text_active,
    fontSize: '0.875em',
    padding: '6px 10px',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const heroLabel = {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.72em',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    display: 'block',
    marginBottom: '4px',
  };

  return (
    <div style={{ maxWidth: '720px' }}>

      {/* ─── Hero Section (T-13: mörkt huvud) ─── */}
      <div className="fr-darkhead" style={{
        position: 'relative',
        background: NEUTRAL.ink,
        borderRadius: t.card_radius,
        padding: '22px 24px',
        marginBottom: '16px',
        overflow: 'hidden',
      }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: '-150px', right: '-70px', width: '360px', height: '360px', borderRadius: '999px', background: t.hero_glow, opacity: 0.30, filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px' }}>
          <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', padding: 0, fontSize: '12px', fontFamily: 'inherit' }}>
            ← Övningar
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !hero.title.trim()} style={{ ...darkPrimary(t), opacity: saving || !hero.title.trim() ? 0.6 : 1 }}>
            {saving ? 'Sparar…' : 'Spara övning'}
          </button>
        </div>

        <div style={{ position: 'relative' }}>
          <input
            value={hero.title}
            onChange={e => setHero(h => ({ ...h, title: e.target.value }))}
            placeholder="Övningens namn..."
            style={heroTitleInput}
          />
        </div>

        <textarea
          value={hero.description}
          onChange={e => setHero(h => ({ ...h, description: e.target.value }))}
          placeholder="Beskriv övningen kort..."
          rows={2}
          style={heroTextareaStyle}
        />

        <div style={{ position: 'relative', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={heroLabel}>Kategori</label>
            <input value={hero.category} onChange={e => setHero(h => ({ ...h, category: e.target.value }))} placeholder="T.ex. nacke, rygg" style={heroSmallInput} />
          </div>
          <div style={{ flex: '0 0 110px' }}>
            <label style={heroLabel}>Tid (min)</label>
            <input type="number" min="0" value={hero.duration} onChange={e => setHero(h => ({ ...h, duration: e.target.value }))} placeholder="–" style={heroSmallInput} />
          </div>
          <div style={{ flex: '0 0 90px' }}>
            <label style={heroLabel}>Sets</label>
            <input type="number" min="0" value={hero.sets} onChange={e => setHero(h => ({ ...h, sets: e.target.value }))} placeholder="–" style={heroSmallInput} />
          </div>
        </div>
      </div>

      {/* ─── Block List ─── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
        {blocks.length === 0 ? (
          <EmptyState
            theme={t}
            title="Inga block ännu"
            message="Lägg till video, steg, bilder eller tips nedan för att bygga övningen"
          />
        ) : (
          blocks.map((block, index) => (
            <SectionCard
              key={block.id}
              theme={t}
              title={BLOCK_TYPE_LABELS[block.type] || block.type}
              meta={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <button
                    onClick={() => moveBlock(block.id, -1)}
                    disabled={index === 0}
                    style={reorderBtnStyle(index === 0)}
                    title="Flytta upp"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveBlock(block.id, 1)}
                    disabled={index === blocks.length - 1}
                    style={reorderBtnStyle(index === blocks.length - 1)}
                    title="Flytta ned"
                  >
                    ▼
                  </button>
                  <Button
                    theme={t}
                    variant="tertiary"
                    size="sm"
                    onClick={() => setEditingBlockId(editingBlockId === block.id ? null : block.id)}
                  >
                    {editingBlockId === block.id ? 'Klar' : 'Redigera'}
                  </Button>
                  <button
                    onClick={() => removeBlock(block.id)}
                    onMouseEnter={e => { e.currentTarget.style.color = STATUS_COLORS.error; e.currentTarget.style.background = STATUS_COLORS.error_bg; }}
                    onMouseLeave={e => { e.currentTarget.style.color = NEUTRAL.faint; e.currentTarget.style.background = 'transparent'; }}
                    style={{ background: 'transparent', border: 'none', color: NEUTRAL.faint, cursor: 'pointer', padding: '4px 8px', borderRadius: '5px', fontSize: '0.8em' }}
                  >
                    Ta bort
                  </button>
                </div>
              }
            >
              {editingBlockId === block.id
                ? renderBlockEditor(block, t, (changes) => updateBlock(block.id, changes), () => uploadImageToBlock(block.id))
                : renderBlockView(block, t)
              }
            </SectionCard>
          ))
        )}
      </div>

      {/* ─── Add Block Section ─── */}
      <div style={{
        border: `2px dashed ${t.card_border}`,
        borderRadius: t.card_radius,
        padding: '20px',
        marginTop: '4px',
      }}>
        <p style={{ textAlign: 'center', color: t.text_secondary, fontSize: '0.75em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 14px' }}>
          Lägg till block
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
          {ADD_BLOCK_TYPES.map(({ type, label }) => (
            <button
              key={type}
              onClick={() => addBlock(type)}
              onMouseEnter={e => { e.currentTarget.style.background = t.accent_soft_bg; e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.color = t.accent_soft_text; }}
              onMouseLeave={e => { e.currentTarget.style.background = t.card_bg; e.currentTarget.style.borderColor = t.card_border; e.currentTarget.style.color = t.text_secondary; }}
              style={{
                background: t.card_bg,
                border: `1px solid ${t.card_border}`,
                borderRadius: t.card_radius,
                padding: '14px 12px',
                cursor: 'pointer',
                fontSize: '0.875em',
                fontWeight: '500',
                color: t.text_secondary,
                transition: 'all 0.12s',
                textAlign: 'center',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
